/* eslint-disable jsx-a11y/label-has-associated-control */
import { useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { Modal } from '@tailwindComponents/Modal';
import { Button } from '@tailwindComponents/Button';
import { Input } from '@tailwindComponents/Input';
import { Checkbox } from '@tailwindComponents/Checkbox';
import { Autocomplete } from '@tailwindComponents/Autocomplete';
import { CollapsibleFormSection } from '@tailwindComponents/CollapsibleFormSection';
import { fleetMapping, fleetSubtitleMapping } from '@views/FleetMaster/types';
import {
  BUSINESS_PROFILE_GET,
  HERE_APIKEY,
  MILEAGE_RATECARD_CREATE,
  MILEAGE_RATECARD_UPDATE,
  RATECARD_GET,
  RATECARDS_GET,
} from '../../constants';
import { useAuthHttp } from '../../hooks';

const CURRENCY_OPTIONS = [
  { label: 'GBP (£)', value: 'GBP' },
  { label: 'EUR (€)', value: 'EUR' },
  { label: 'USD ($)', value: 'USD' },
];

const currencySymbols: Record<string, string> = {
  GBP: '£',
  EUR: '€',
  USD: '$',
};

type GeoLocation = { lat: number; lon: number };

type MileageCard = {
  id: string;
  pricePerMile?: number;
  baseCharge?: number;
  chargeCurrency?: string;
  minimumMiles?: number;
  maximumMiles?: number;
  maximumLoadKg?: number;
  willCollectFromAnywhere?: boolean;
  transportBaseAddress?: string;
  transportBaseLocation?: GeoLocation | null;
  effectiveFrom?: string | null;
  effectiveTo?: string | null;
};

const hasRealCoordinates = (
  location: GeoLocation | null | undefined
): location is GeoLocation =>
  !!location &&
  Math.abs(location.lat) <= 90 &&
  Math.abs(location.lon) <= 180 &&
  !(location.lat === 0 && location.lon === 0);

const formatValidityDate = (isoDate: string) =>
  dayjs(isoDate).format('D MMM YYYY');

const describeValidityPeriod = (
  effectiveFrom: string | null,
  effectiveTo: string | null
) => {
  if (effectiveFrom && effectiveTo) {
    return `Valid from ${formatValidityDate(
      effectiveFrom
    )} until ${formatValidityDate(effectiveTo)}`;
  }
  if (effectiveFrom) return `Valid from ${formatValidityDate(effectiveFrom)}`;
  if (effectiveTo) return `Valid until ${formatValidityDate(effectiveTo)}`;
  return null;
};

type AddressOption = {
  id: string;
  label: string;
  city?: string;
  country?: string;
};

export interface PricePerMileModalProps {
  vehicleType: string | null;
  isOpen: boolean;
  onClose: () => void;
  onSaved?: () => void;
}

export function PricePerMileModal({
  vehicleType,
  isOpen,
  onClose,
  onSaved,
}: PricePerMileModalProps) {
  const authHttp = useAuthHttp();

  const [isLoading, setIsLoading] = useState(false);
  const [hasLoadFailed, setHasLoadFailed] = useState(false);
  const [reloadCounter, setReloadCounter] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  const [existingCardId, setExistingCardId] = useState<string | null>(null);
  const [baseLocation, setBaseLocation] = useState<GeoLocation | null>(null);
  const [locationAddressLabel, setLocationAddressLabel] = useState<
    string | null
  >(null);
  const [categoryCardCount, setCategoryCardCount] = useState(0);
  const [hasLoadedMileageCardWithMaximumLoad, setHasLoadedMileageCardWithMaximumLoad] =
    useState(false);
  const [baseAddress, setBaseAddress] = useState('');
  const [addressOptions, setAddressOptions] = useState<AddressOption[]>([]);

  const [pricePerMile, setPricePerMile] = useState('');
  const [baseCharge, setBaseCharge] = useState('');
  const [minimumMiles, setMinimumMiles] = useState('');
  const [maximumMiles, setMaximumMiles] = useState('');
  const [maximumLoadKg, setMaximumLoadKg] = useState('');
  const [willCollectFromAnywhere, setWillCollectFromAnywhere] = useState(false);
  const [currency, setCurrency] = useState('GBP');
  const [loadedEffectiveFrom, setLoadedEffectiveFrom] = useState<string | null>(
    null
  );
  const [loadedEffectiveTo, setLoadedEffectiveTo] = useState<string | null>(
    null
  );

  const openRequestRef = useRef(0);
  const addressSelectRequestRef = useRef(0);

  const displayName = vehicleType
    ? fleetMapping[vehicleType] || vehicleType
    : '';
  const displaySubtitle = vehicleType
    ? fleetSubtitleMapping[vehicleType] || ''
    : '';
  const symbol = currencySymbols[currency] || '£';
  const validityPeriod = describeValidityPeriod(
    loadedEffectiveFrom,
    loadedEffectiveTo
  );

  const resetForm = () => {
    setExistingCardId(null);
    setBaseLocation(null);
    setLocationAddressLabel(null);
    setCategoryCardCount(0);
    setHasLoadedMileageCardWithMaximumLoad(false);
    setBaseAddress('');
    setAddressOptions([]);
    setPricePerMile('');
    setBaseCharge('');
    setMinimumMiles('');
    setMaximumMiles('');
    setMaximumLoadKg('');
    setWillCollectFromAnywhere(false);
    setCurrency('GBP');
    setLoadedEffectiveFrom(null);
    setLoadedEffectiveTo(null);
    setHasLoadFailed(false);
    setSaveError(null);
    setValidationError(null);
  };

  const prefillFromCard = (card: MileageCard) => {
    setExistingCardId(card.id);
    if (hasRealCoordinates(card.transportBaseLocation)) {
      setBaseLocation(card.transportBaseLocation);
      setLocationAddressLabel(card.transportBaseAddress ?? null);
    }
    if (card.transportBaseAddress) setBaseAddress(card.transportBaseAddress);
    if (card.pricePerMile != null) setPricePerMile(String(card.pricePerMile));
    if (card.baseCharge != null && card.baseCharge > 0)
      setBaseCharge(String(card.baseCharge));
    if (card.chargeCurrency) setCurrency(card.chargeCurrency);
    if (card.minimumMiles != null) setMinimumMiles(String(card.minimumMiles));
    if (card.maximumMiles != null) setMaximumMiles(String(card.maximumMiles));
    if (card.maximumLoadKg != null)
      setMaximumLoadKg(String(card.maximumLoadKg));
    setWillCollectFromAnywhere(card.willCollectFromAnywhere === true);
    setLoadedEffectiveFrom(card.effectiveFrom ?? null);
    setLoadedEffectiveTo(card.effectiveTo ?? null);
  };

  const fetchAddressSuggestions = async (
    input: string
  ): Promise<AddressOption[]> => {
    const response = await fetch(
      `https://autosuggest.search.hereapi.com/v1/autosuggest?apiKey=${HERE_APIKEY}&q=${encodeURIComponent(
        input
      )}&at=51.5074,-0.1278&in=countryCode:GBR`
    );
    const data = await response.json();
    const items: {
      id: string;
      address: { label: string; city?: string; countryName?: string };
    }[] = data.items ?? [];
    return items.map((item) => ({
      id: item.id,
      label: item.address.label,
      city: item.address.city,
      country: item.address.countryName,
    }));
  };

  const fetchLocationForAddressId = async (
    id: string
  ): Promise<GeoLocation | null> => {
    const response = await fetch(
      `https://lookup.search.hereapi.com/v1/lookup?id=${id}&apiKey=${HERE_APIKEY}`
    );
    const data = await response.json();
    if (!data?.position) return null;
    const { lat, lng: lon } = data.position;
    return { lat, lon };
  };

  const resolvePrefilledAddress = async (
    address: string
  ): Promise<{ label: string; location: GeoLocation } | null> => {
    try {
      const suggestions = await fetchAddressSuggestions(address);
      const topMatch = suggestions[0];
      if (!topMatch) return null;
      const location = await fetchLocationForAddressId(topMatch.id);
      if (!location) return null;
      return { label: topMatch.label, location };
    } catch (error) {
      console.error('Error resolving prefilled address:', error);
      return null;
    }
  };

  useEffect(() => {
    if (!isOpen || !vehicleType) return;
    resetForm();
    setIsLoading(true);
    openRequestRef.current += 1;
    const requestId = openRequestRef.current;
    const isStale = () => openRequestRef.current !== requestId;

    (async () => {
      try {
        const { data: overview } = await authHttp.get(RATECARDS_GET);
        if (isStale()) return;
        const mileageGroups: {
          vehicleType: string;
          vehicleRateCards?: { rateCardId: string }[];
        }[] = overview?.transportRatecards?.mileage ?? [];
        const group = mileageGroups.find((g) => g.vehicleType === vehicleType);
        const cards = group?.vehicleRateCards ?? [];
        const cardId = cards[0]?.rateCardId ?? null;

        if (cardId) {
          const { data } = await authHttp.get(
            `${RATECARD_GET}?type=mileage&id=${cardId}`
          );
          if (isStale()) return;
          const card: MileageCard | undefined = data?.mileageRateCard;
          if (card) {
            prefillFromCard({ ...card, id: cardId });
            setHasLoadedMileageCardWithMaximumLoad(card.maximumLoadKg != null);
          } else {
            setExistingCardId(cardId);
          }
          setCategoryCardCount(cards.length);
        } else {
          const { data } = await authHttp.get(BUSINESS_PROFILE_GET);
          if (isStale()) return;
          const profileAddress = data?.businessProfile?.address;
          if (profileAddress) {
            const resolved = await resolvePrefilledAddress(profileAddress);
            if (isStale()) return;
            if (resolved) {
              setBaseAddress(resolved.label);
              setBaseLocation(resolved.location);
              setLocationAddressLabel(resolved.label);
            }
          }
        }
      } catch (error) {
        if (isStale()) return;
        console.error('Failed to load price per mile tariff:', error);
        setHasLoadFailed(true);
      } finally {
        if (!isStale()) setIsLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, vehicleType, reloadCounter]);

  const handleFetchSuggestions = (input: string) => {
    if (input.length < 3) {
      setAddressOptions([]);
      return;
    }

    fetchAddressSuggestions(input)
      .then((items) => setAddressOptions(items))
      .catch((error) => {
        console.error('Error fetching suggestions:', error);
      });
  };

  const handleAddressSelect = async (option: AddressOption | null) => {
    addressSelectRequestRef.current += 1;
    const requestId = addressSelectRequestRef.current;
    const isStale = () => addressSelectRequestRef.current !== requestId;

    setBaseLocation(null);
    setLocationAddressLabel(null);

    if (!option) return;
    setBaseAddress(option.label);
    try {
      const location = await fetchLocationForAddressId(option.id);
      if (isStale()) return;
      if (location) {
        setBaseLocation(location);
        setLocationAddressLabel(option.label);
      }
    } catch (error) {
      console.error('Error fetching position:', error);
    }
  };

  const handleAddressInputChange = (value: string) => {
    addressSelectRequestRef.current += 1;
    setBaseAddress(value);
    if (locationAddressLabel !== null && value !== locationAddressLabel) {
      setBaseLocation(null);
      setLocationAddressLabel(null);
    }
    handleFetchSuggestions(value);
  };

  const describeSaveFailure = (error: unknown): string => {
    const responseData = (
      error as { response?: { data?: { errors?: Record<string, string[]> } } }
    )?.response?.data;
    const fieldMessages = responseData?.errors
      ? Object.values(responseData.errors).flat().join(' ')
      : '';
    return fieldMessages || 'Failed to save. Please try again.';
  };

  const handleClose = () => {
    if (isSaving) return;
    onClose();
  };

  const handleSave = async () => {
    const trimmedPricePerMile = pricePerMile.trim();
    const pricePerMileNumber = Number(trimmedPricePerMile);
    if (
      trimmedPricePerMile === '' ||
      !Number.isFinite(pricePerMileNumber) ||
      pricePerMileNumber <= 0
    ) {
      setValidationError('Price per mile is required and must be more than 0.');
      return;
    }

    let baseChargeNumber = 0;
    if (baseCharge.trim() !== '') {
      baseChargeNumber = Number(baseCharge);
      if (!Number.isFinite(baseChargeNumber) || baseChargeNumber < 0) {
        setValidationError('Base charge must be 0 or more.');
        return;
      }
    }

    let minimumMilesNumber: number | null = null;
    if (minimumMiles.trim() !== '') {
      minimumMilesNumber = Number(minimumMiles);
      if (!Number.isFinite(minimumMilesNumber) || minimumMilesNumber < 0) {
        setValidationError('Minimum miles must be 0 or more.');
        return;
      }
    }

    let maximumMilesNumber: number | null = null;
    if (maximumMiles.trim() !== '') {
      maximumMilesNumber = Number(maximumMiles);
      if (!Number.isFinite(maximumMilesNumber) || maximumMilesNumber <= 0) {
        setValidationError('Maximum miles must be more than 0.');
        return;
      }
    }

    if (
      minimumMilesNumber != null &&
      maximumMilesNumber != null &&
      maximumMilesNumber < minimumMilesNumber
    ) {
      setValidationError(
        'Maximum miles must be greater than or equal to minimum miles.'
      );
      return;
    }

    const trimmedMaximumLoadKg = maximumLoadKg.trim();
    const maximumLoadKgNumber = Number(trimmedMaximumLoadKg);
    if (
      trimmedMaximumLoadKg === '' ||
      !Number.isFinite(maximumLoadKgNumber) ||
      maximumLoadKgNumber <= 0
    ) {
      setValidationError(
        'Maximum load (kg) is required and must be more than 0.'
      );
      return;
    }

    if (!baseLocation || baseAddress !== locationAddressLabel) {
      setValidationError(
        'Select a transport base address from the suggestions to set its location.'
      );
      return;
    }

    setValidationError(null);
    setSaveError(null);
    setIsSaving(true);
    try {
      const endpoint = existingCardId
        ? MILEAGE_RATECARD_UPDATE
        : MILEAGE_RATECARD_CREATE;
      await authHttp.post(endpoint, {
        rateCard: {
          ...(existingCardId ? { id: existingCardId } : {}),
          vehicleType,
          transportBaseAddress: baseAddress,
          transportBaseLocation: baseLocation,
          chargeCurrency: currency,
          pricePerMile: pricePerMileNumber,
          baseCharge: baseChargeNumber,
          minimumMiles: minimumMilesNumber,
          maximumMiles: maximumMilesNumber,
          maximumLoadKg: maximumLoadKgNumber,
          willCollectFromAnywhere,
          effectiveFrom: loadedEffectiveFrom,
          effectiveTo: loadedEffectiveTo,
        },
      });
      onSaved?.();
      onClose();
    } catch (error) {
      console.error('Failed to save price per mile tariff:', error);
      setSaveError(describeSaveFailure(error));
    } finally {
      setIsSaving(false);
    }
  };

  const selectClassName =
    'block w-full rounded-md bg-white py-1.5 px-3 text-sm text-gray-950 outline-1 -outline-offset-1 outline-gray-200 border-gray-200 focus:outline-2 focus:-outline-offset-2 focus:outline-blue-200';

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={handleClose}
      title={`Price per Mile Tariff — ${displayName}`}
    >
      <div className='flex flex-col gap-4 w-full sm:w-[28rem]'>
        <p className='text-sm text-gray-500 -mt-2'>
          Applies to all {displaySubtitle || displayName} vehicles in your
          fleet.
        </p>

        {isLoading && (
          <p className='text-sm text-gray-400 py-8 text-center'>
            Loading current rates...
          </p>
        )}

        {!isLoading && hasLoadFailed && (
          <div className='flex flex-col items-center gap-3 py-6'>
            <p className='text-sm text-red-600 text-center'>
              Could not load the current rates for this category. Saving now
              could create a duplicate tariff, so please retry first.
            </p>
            <Button
              label='Try again'
              intent='secondary'
              onClick={() => setReloadCounter((count) => count + 1)}
            />
          </div>
        )}

        {!isLoading && !hasLoadFailed && (
          <>
            {validityPeriod && (
              <p className='text-sm text-gray-600'>{validityPeriod}</p>
            )}

            {categoryCardCount > 1 && (
              <p className='text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3'>
                This category has {categoryCardCount} price-per-mile tariff
                cards. You are editing the most recent — the others still appear
                in quotes.
              </p>
            )}

            {existingCardId !== null && !hasLoadedMileageCardWithMaximumLoad && (
              <p className='text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3'>
                This rate gives no quotes until you set a maximum load.
              </p>
            )}

            <Autocomplete
              label='Transport base address'
              options={addressOptions}
              value={baseAddress}
              onChange={(option) =>
                handleAddressSelect(option as AddressOption | null)
              }
              onInputChange={handleAddressInputChange}
              placeholder='Start typing an address...'
              helperText='Pick a suggested address so we know where this vehicle is based.'
            />

            <div className='flex flex-col gap-1'>
              <Checkbox
                label='Collect from anywhere'
                checked={willCollectFromAnywhere}
                onChange={(e) => setWillCollectFromAnywhere(e.target.checked)}
                aria-describedby='collect-from-anywhere-help'
              />
              <p
                id='collect-from-anywhere-help'
                className='text-sm text-gray-500'
              >
                Tick this if you collect at any distance from your base.
                You&apos;re paid per mile from collection to delivery only, not
                for travelling to the collection point. Leave it unticked and
                this rate only quotes for collections within 50 km (about 30
                miles) of your base.
              </p>
            </div>

            <div className='grid grid-cols-2 gap-3'>
              <Input
                label={`Price per mile (${symbol})`}
                name='pricePerMile'
                type='number'
                step='0.01'
                placeholder='0.00'
                value={pricePerMile}
                onChange={(e) => setPricePerMile(e.target.value)}
              />
              <Input
                label={`Base charge (${symbol})`}
                name='baseCharge'
                type='number'
                step='0.01'
                placeholder='0.00'
                optional
                value={baseCharge}
                onChange={(e) => setBaseCharge(e.target.value)}
                helperText='A fixed amount added once to every job, on top of the miles charged (including any minimum miles). Leave blank for none.'
              />
            </div>

            <div className='grid grid-cols-2 gap-3'>
              <Input
                label='Minimum miles'
                name='minimumMiles'
                type='number'
                step='1'
                placeholder='0'
                optional
                value={minimumMiles}
                onChange={(e) => setMinimumMiles(e.target.value)}
              />
              <Input
                label='Maximum miles'
                name='maximumMiles'
                type='number'
                step='1'
                placeholder='0'
                optional
                value={maximumMiles}
                onChange={(e) => setMaximumMiles(e.target.value)}
                helperText='The longest collection-to-delivery journey this rate quotes for. It does not limit how far you travel to collect.'
              />
            </div>

            <Input
              label='Maximum load (kg)'
              name='maximumLoadKg'
              type='number'
              step='1'
              placeholder='0'
              value={maximumLoadKg}
              onChange={(e) => setMaximumLoadKg(e.target.value)}
              helperText='The heaviest load this vehicle carries. Heavier loads get no quote from this rate.'
            />

            <CollapsibleFormSection
              title='Advanced'
              description='Currency'
              optional={false}
              defaultOpen={currency !== 'GBP'}
            >
              <div className='flex flex-col gap-1'>
                <label
                  htmlFor='pricePerMileCurrency'
                  className='text-sm font-medium text-gray-700'
                >
                  Currency
                </label>
                <select
                  id='pricePerMileCurrency'
                  className={selectClassName}
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                >
                  {CURRENCY_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </CollapsibleFormSection>

            {validationError && (
              <p className='text-sm text-red-600'>{validationError}</p>
            )}
            {saveError && <p className='text-sm text-red-600'>{saveError}</p>}

            <div className='flex justify-end gap-2 pt-1'>
              <Button
                label='Cancel'
                intent='secondary'
                onClick={handleClose}
                disabled={isSaving}
              />
              <Button
                label={isSaving ? 'Saving...' : 'Save'}
                intent='primary'
                onClick={handleSave}
                disabled={isSaving}
              />
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
